import { Injectable, inject } from "@angular/core";
import { firstValueFrom } from "rxjs";
import { FileUploadService } from "./file-upload.service";
import { SearchQueryService } from "../../../compound-components/services/search/search-query.service";
import { embeddingFeatureToSearchItem } from "../../../compound-components/services/search/requests/implementations/api-search.service";
import { CustomElementsLauncherService } from "../../../compound-components/services/custom-elements-launcher.service";
import type { DetectionApiResponse } from "../../../compound-components/services/detection/detection.communicator.service";
import { SearchType } from "../../../compound-components/model/search/search-type.enum";
import { ToolbarService } from "../../../viewer-components/services/internal-services/toolbar.service";
import { ToastService } from "../../../viewer-components/services/internal-services/toast.service";
import { TOAST_STATUS } from "../../../viewer-components/model/ToastConstants";
import { GeoService } from "../../../viewer-components/services/internal-services/geo.service";
import { toolbarOptions } from "../../../shared/model/toolbar-constants";
import { resolveLayerContract } from "../model/evidence-layer.types";
import type { ContentBlockData } from "../model/block.types";

/**
 * Chat-side bridge that pushes an agent-produced result block into its viewer component
 * (viewer-handoff plan F1.5). Both results travel as `kind: "geometry"` blocks whose GeoJSON
 * lives in the file store (`data.ref`) — embeddings results land in Search Enhanced Results
 * (root-scoped `SearchQueryService`, injected directly) and elements results in Custom
 * Elements (viewer-scoped `DetectionService`, reachable only through the root
 * `CustomElementsLauncherService` bridge, same pattern as the search-summary-panel).
 *
 * Modeled on `EvidenceLayerService`: always fetches `block.ref` (never trusts the inline
 * value — results above the 500 KB auto-load threshold and elements payloads with pixel
 * masks are large), routes failures through `console.error` + a toast, and never throws
 * into the caller's stream.
 */
@Injectable({ providedIn: "root" })
export class ViewerHandoffService {
  readonly #fileUpload = inject(FileUploadService);

  readonly #search = inject(SearchQueryService);

  readonly #elementsLauncher = inject(CustomElementsLauncherService);

  readonly #toolbar = inject(ToolbarService);

  readonly #toast = inject(ToastService);

  /** Pushes an embeddings result block into the Search Enhanced Results panel. */
  async toEmbeddings(block: ContentBlockData): Promise<void> {
    const geojson = await this.#fetchRefGeoJson(block);
    if (!geojson) return;

    const items = (geojson.features ?? []).map((feature) => embeddingFeatureToSearchItem(feature));
    const params = this.#requestedParams(block);
    this.#search.loadExternalResults({
      items,
      query: params["search_text"],
      wkt: params["wkt"],
      searchType: SearchType.TEXT,
    });
    this.#toolbar.enableTool(toolbarOptions.SEARCH_ENHANCED_RESULTS);
  }

  /** Pushes an elements result block into the Custom Elements panel (results stage). */
  async toElements(block: ContentBlockData): Promise<void> {
    const geojson = await this.#fetchRefGeoJson(block);
    if (!geojson) return;

    const wkt = this.#requestedParams(block)["wkt"];
    let areas: { wkt: string }[];
    let visualWkt: string;
    if (wkt) {
      // The request area travels as POLYGON/MULTIPOLYGON WKT — same flattening the
      // search-summary-panel applies so every child polygon becomes a detection target.
      areas = GeoService.splitMultiPolygon(wkt).map((polygon) => ({ wkt: polygon }));
      visualWkt = wkt;
    } else {
      // Fallback: bounding envelope of the detected features themselves.
      // GeoService.featureCollectionBbox already returns the envelope as WKT.
      const envelopeWkt = GeoService.featureCollectionBbox(geojson);
      if (!envelopeWkt) {
        this.#fail("ViewerHandoffService: elements block has neither requested_params.wkt nor a computable bbox", block);
        return;
      }
      visualWkt = envelopeWkt;
      areas = [{ wkt: envelopeWkt }];
    }

    this.#elementsLauncher.launch({
      areas,
      visualWkt,
      label: resolveLayerContract(block)?.label ?? block.label ?? undefined,
      results: geojson as DetectionApiResponse,
    });
  }

  /**
   * Fetches and parses the block's file ref as GeoJSON. Always goes to the wire — the inline
   * value is ignored because refs above 500 KB are not auto-loaded by the card. Every failure
   * path logs + toasts and resolves null.
   */
  async #fetchRefGeoJson(block: ContentBlockData): Promise<FeatureCollectionGeoJson | null> {
    const ref = block.ref;
    if (!ref) {
      this.#fail("ViewerHandoffService: result block has no file ref — nothing to hand off", block);
      return null;
    }
    try {
      const result = await firstValueFrom(
        this.#fileUpload.fetchFileContent(ref.url, ref.mime_type, {
          filename: ref.filename,
          size_bytes: ref.size_bytes,
        }),
      );
      if (result.type !== "geojson") {
        this.#fail(`ViewerHandoffService: result arrived as ${result.type}, expected geojson`, block);
        return null;
      }
      return result.geojson as FeatureCollectionGeoJson;
    } catch (err) {
      this.#fail(`ViewerHandoffService: result fetch failed for ${ref.url}`, block, err);
      return null;
    }
  }

  /** `data.metadata.requested_params` — open string map (wkt / search_text / labels). */
  #requestedParams(block: ContentBlockData): Record<string, string> {
    const metadata = (block.metadata ?? {}) as Record<string, unknown>;
    return (metadata["requested_params"] ?? {}) as Record<string, string>;
  }

  /** User-reachable failures surface through the toast system; console logging stays alongside. */
  #fail(message: string, block: ContentBlockData, err?: unknown): void {
    if (err !== undefined) console.error(message, err, block);
    else console.error(message, block);
    this.#toast.setNotification({ message, type: TOAST_STATUS.ERROR });
  }
}

/** Minimal structural view of the fetched GeoJSON — avoids leaking `any` into the public API. */
interface FeatureCollectionGeoJson {
  type: "FeatureCollection";
  features: any[];
}
